import { useState } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { ShieldCheck } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Tabs, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { PageTabsList } from '@/components/custom/page-tabs-list';
import { getPlatformClient } from '@/lib/api-clients';
import { queryKeys } from '@/lib/query-keys';

type PlatformUserListStatus = 'active' | 'inactive';

export const Route = createFileRoute('/platform/admin-users')({
  component: AdminUsersPage,
});

function AdminUsersPage(): React.ReactElement {
  const [showCreate, setShowCreate] = useState(false);
  const [tab, setTab] = useState<PlatformUserListStatus>('active');

  return (
    <div className="space-y-4">
      <header className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold">Platform users</h2>
          <p className="text-muted-foreground text-sm">
            People with cross-org impersonation access. Revoking deletes their sessions.
          </p>
        </div>
        <Button onClick={() => setShowCreate(true)}>Add platform user</Button>
      </header>

      <Tabs value={tab} onValueChange={(v) => setTab(v as PlatformUserListStatus)}>
        <PageTabsList>
          <TabsTrigger value="active">Active</TabsTrigger>
          <TabsTrigger value="inactive">Inactive</TabsTrigger>
        </PageTabsList>
        <TabsContent value="active">
          <PlatformUsersTable status="active" />
        </TabsContent>
        <TabsContent value="inactive">
          <PlatformUsersTable status="inactive" />
        </TabsContent>
      </Tabs>

      {showCreate && <CreatePlatformUserDialog onClose={() => setShowCreate(false)} />}
    </div>
  );
}

function PlatformUsersTable({ status }: { status: PlatformUserListStatus }): React.ReactElement {
  const queryClient = useQueryClient();

  const platformUsers = useQuery({
    queryKey: queryKeys.platform.adminUsers(status),
    queryFn: ({ signal }) => getPlatformClient().listPlatformUsers(status, signal),
  });

  const revoke = useMutation({
    mutationFn: (id: string) => getPlatformClient().revokePlatformUser(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.platform.all });
      toast.success('Platform access revoked');
    },
  });

  if (platformUsers.isLoading) {
    return <div className="text-muted-foreground py-4">Loading…</div>;
  }
  if (platformUsers.isError) {
    return <div className="text-destructive py-4">Failed to load platform users.</div>;
  }
  if (!platformUsers.data) return <></>;

  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>Email</TableHead>
          <TableHead>Name</TableHead>
          <TableHead>Role</TableHead>
          <TableHead className="text-right">Actions</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {platformUsers.data.length === 0 && (
          <TableRow>
            <TableCell colSpan={4} className="text-muted-foreground text-center">
              {status === 'active' ? 'No active platform users.' : 'No revoked platform users.'}
            </TableCell>
          </TableRow>
        )}
        {platformUsers.data.map((u) => (
          <TableRow key={u.id}>
            <TableCell className="font-medium">{u.email}</TableCell>
            <TableCell>{u.name}</TableCell>
            <TableCell>
              <code className="text-xs">{u.platformRole}</code>
            </TableCell>
            <TableCell className="text-right">
              {status === 'active' ? (
                <Button
                  size="sm"
                  variant="destructive"
                  disabled={revoke.isPending}
                  onClick={() => {
                    if (confirm(`Revoke platform access for ${u.email}?`)) {
                      revoke.mutate(u.id);
                    }
                  }}
                >
                  Revoke
                </Button>
              ) : (
                <span className="text-muted-foreground text-xs">Revoked</span>
              )}
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

export function CreatePlatformUserDialog({ onClose }: { onClose: () => void }): React.ReactElement {
  const queryClient = useQueryClient();
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [role, setRole] = useState<'platform_admin'>('platform_admin');

  const create = useMutation({
    mutationFn: () =>
      getPlatformClient().createPlatformUser({
        email,
        name,
        platformRole: role,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.platform.all });
      toast.success('Platform user added');
      onClose();
    },
  });

  const emailLooksValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());
  const canSubmit = !create.isPending && emailLooksValid && name.trim().length > 0;

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-[480px]">
        <DialogHeader>
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary/10 text-primary">
              <ShieldCheck className="h-5 w-5" aria-hidden="true" />
            </div>
            <div className="space-y-1">
              <DialogTitle>Add platform user</DialogTitle>
              <DialogDescription>
                Add a teammate to the VagusX support directory so they can impersonate users across
                organizations.
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <div className="space-y-4 pt-2">
          <div className="space-y-1.5">
            <Label htmlFor="platform-user-email">Email</Label>
            <Input
              id="platform-user-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@vagusx.ai"
              autoFocus
              autoComplete="off"
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="platform-user-name">Full name</Label>
            <Input
              id="platform-user-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Jane Reviewer"
              autoComplete="off"
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="platform-user-role">Role</Label>
            <Select value={role} onValueChange={(v) => setRole(v as 'platform_admin')}>
              <SelectTrigger id="platform-user-role" className="w-full justify-start">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="platform_admin">Platform admin</SelectItem>
              </SelectContent>
            </Select>
            <p className="text-muted-foreground text-xs">
              Full cross-org impersonation + manage platform users.
            </p>
          </div>
        </div>

        <DialogFooter className="gap-2 sm:gap-2">
          <Button variant="outline" onClick={onClose} disabled={create.isPending}>
            Cancel
          </Button>
          <Button disabled={!canSubmit} onClick={() => create.mutate()}>
            {create.isPending ? 'Adding…' : 'Add user'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
